import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Clock, Zap, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { MaskedLines } from "@/components/MaskedLines";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { useLang } from "@/i18n";

const inputCls =
  "w-full rounded-xl border border-slate-700/80 bg-slate-900/70 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors focus:border-sky-400/70 focus:ring-2 focus:ring-sky-500/20";

const SUBJECT_VALUES = ["compatibilite", "commande", "entretien", "garantie", "autre"];

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.toastOk"), { description: t("contact.toastOkSub") });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error(t("contact.toastErr"));
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="mx-auto max-w-7xl px-4 pt-32 pb-24 sm:px-8 lg:px-16">
      <Seo title={`${t("contact.eyebrow")} | NordiGuard`} description={t("faq.sub")} />

      <header className="pt-8">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400">{t("contact.eyebrow")}</p>
        <h1 className="font-display mt-3 text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
          <MaskedLines lines={[t("contact.l1"), t("contact.l2")]} />
        </h1>
      </header>

      <div className="mt-14 grid gap-10 lg:grid-cols-5">
        <Reveal className="lg:col-span-3">
          <form onSubmit={submit} className="glacial-card space-y-5 p-8" data-testid="contact-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <input data-testid="contact-input-name" required placeholder={t("contact.name")} value={form.name} onChange={set("name")} className={inputCls} />
              <input data-testid="contact-input-email" required type="email" placeholder={t("contact.email")} value={form.email} onChange={set("email")} className={inputCls} />
            </div>
            <select data-testid="contact-select-subject" required value={form.subject} onChange={set("subject")} className={inputCls}>
              <option value="" disabled>{t("contact.subjectPh")}</option>
              {SUBJECT_VALUES.map((v, i) => (
                <option key={v} value={v}>{t("contact.subjects")[i]}</option>
              ))}
            </select>
            <textarea
              data-testid="contact-input-message"
              required
              rows={6}
              placeholder={t("contact.messagePh")}
              value={form.message}
              onChange={set("message")}
              className={inputCls}
            />
            <button
              type="submit"
              data-testid="contact-form-submit"
              disabled={sending}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-sky-400 py-4 text-sm font-bold text-slate-950 transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_0_30px_rgba(56,189,248,0.5)] disabled:opacity-60"
            >
              {sending && <Loader2 size={16} className="animate-spin" />}
              {sending ? t("contact.sending") : t("contact.send")}
            </button>
          </form>
        </Reveal>

        <div className="space-y-6 lg:col-span-2">
          <Reveal delay={0.1}>
            <div className="glacial-card space-y-5 p-7">
              <div className="flex items-start gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-sky-500/25 bg-sky-500/10 text-sky-400">
                  <Mail size={18} />
                </span>
                <div>
                  <h3 className="font-display text-sm font-bold text-slate-50">{t("contact.emailTitle")}</h3>
                  <a href="mailto:info@nordiguard.com" data-testid="contact-email-link" className="text-sm text-slate-400 transition-colors hover:text-sky-300">
                    info@nordiguard.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-sky-500/25 bg-sky-500/10 text-sky-400">
                  <Clock size={18} />
                </span>
                <div>
                  <h3 className="font-display text-sm font-bold text-slate-50">{t("contact.hoursTitle")}</h3>
                  <p className="text-sm text-slate-400">{t("contact.hoursVal")}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-sky-500/25 bg-sky-500/10 text-sky-400">
                  <Zap size={18} />
                </span>
                <div>
                  <h3 className="font-display text-sm font-bold text-slate-50">{t("contact.responseTitle")}</h3>
                  <p className="text-sm text-slate-400">{t("contact.responseVal")}</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="glacial-card relative h-64 overflow-hidden" data-testid="contact-map-visual">
              <svg className="absolute inset-0 h-full w-full" viewBox="0 0 400 260" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
                <rect width="400" height="260" fill="#070C16" />
                {[...Array(9)].map((_, i) => (
                  <line key={`v${i}`} x1={i * 50} y1="0" x2={i * 50} y2="260" stroke="#1E293B" strokeWidth="1" />
                ))}
                {[...Array(6)].map((_, i) => (
                  <line key={`h${i}`} x1="0" y1={i * 52} x2="400" y2={i * 52} stroke="#1E293B" strokeWidth="1" />
                ))}
                <path d="M0 180 Q120 140 200 160 T400 120" stroke="#38BDF8" strokeWidth="2.5" fill="none" opacity="0.45" />
                <path d="M60 0 Q90 120 150 260" stroke="#38BDF8" strokeWidth="2" fill="none" opacity="0.25" />
              </svg>
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <span className="pulse-ring relative flex h-12 w-12 items-center justify-center rounded-full bg-sky-400 text-slate-950 shadow-[0_0_30px_rgba(56,189,248,0.6)]">
                  <MapPin size={20} />
                </span>
              </div>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full border border-sky-500/20 bg-slate-950/80 px-4 py-1.5 text-xs font-semibold text-sky-300 backdrop-blur">
                {t("contact.mapLabel")}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
